import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/ForgotPassword.css";
import { API_BASE_URL } from "../config/constants";

function ForgotPasswordPage() {
  const [stage, setStage] = useState("email"); // email, otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // loading, success, error
  const [popupMessage, setPopupMessage] = useState("");
  const [animationStage, setAnimationStage] = useState(0); // 0: none, 1: loading, 2: success
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  // Email validation
  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  // Password validation (matches backend: 8-25 chars, 1 number, 1 special char)
  const validatePassword = (password) => {
    const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,25}$/;
    return regex.test(password);
  };

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) {
      setPopupType("error");
      setPopupMessage("Please enter a valid email address.");
      setShowPopup(true);
      return;
    }

    try {
      setAnimationStage(1);
      setPopupType("loading");
      setPopupMessage("Sending OTP...");
      setShowPopup(true);

      await axios.post(`${API_BASE_URL}/forgot-password`, {
        email, // Changed from userEmail to email
      });

      setTimeout(() => {
        setAnimationStage(2);
        setPopupType("success");
        setPopupMessage("OTP sent to your email.");
      }, 1000);

      setTimeout(() => {
        setShowPopup(false);
        setAnimationStage(0);
        setStage("otp");
        startResendTimer();
      }, 4000);
    } catch (err) {
      setShowPopup(false);
      setAnimationStage(0);
      const message = err.response?.data || "Failed to send OTP.";
      setPopupType("error");
      setPopupMessage(message);
      setShowPopup(true);
    }
  };

  const handleResendOtp = async () => {
    if (resendAttempts >= 2) {
      setPopupType("error");
      setPopupMessage("Maximum resend attempts reached.");
      setShowPopup(true);
      return;
    }

    try {
      setAnimationStage(1);
      setPopupType("loading");
      setPopupMessage("Resending OTP...");
      setShowPopup(true);

      await axios.post(`${API_BASE_URL}/forgot-password`, {
        email, // Changed from userEmail to email
      });

      setTimeout(() => {
        setAnimationStage(2);
        setPopupType("success");
        setPopupMessage("OTP resent to your email.");
      }, 1000);

      setTimeout(() => {
        setShowPopup(false);
        setAnimationStage(0);
        setResendAttempts((prev) => prev + 1);
        startResendTimer();
        if (resendAttempts === 1) {
          setPopupType("error");
          setPopupMessage("Only 1 attempt left.");
          setShowPopup(true);
        }
      }, 4000);
    } catch (err) {
      setShowPopup(false);
      setAnimationStage(0);
      const message = err.response?.data || "Failed to resend OTP.";
      setPopupType("error");
      setPopupMessage(message);
      setShowPopup(true);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!validatePassword(newPassword)) {
      setPopupType("error");
      setPopupMessage(
        "Password must be 8-25 characters, include 1 number and 1 special character."
      );
      setShowPopup(true);
      return;
    }

    try {
      setAnimationStage(1);
      setPopupType("loading");
      setPopupMessage("Verifying OTP...");
      setShowPopup(true);

      await axios.post(`${API_BASE_URL}/verify-otp`, {
        email, // Changed from userEmail to email
        otp,
        newPassword,
      });

      setTimeout(() => {
        setAnimationStage(2);
        setPopupType("success");
        setPopupMessage("Password reset successful.");
      }, 1000);

      setTimeout(() => {
        setShowPopup(false);
        navigate("/login");
      }, 4000);
    } catch (err) {
      setShowPopup(false);
      setAnimationStage(0);
      const message = err.response?.data || "Failed to verify OTP.";
      setPopupType("error");
      setPopupMessage(message);
      setShowPopup(true);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-box">
        <button className="auth-back-btn" onClick={() => navigate("/")} type="button">
          Back to Home
        </button>
        <div className="brand-section" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <h1 className="brand-name">A M I N I I</h1>
          <p className="brand-subtitle">C L O T H I N G</p>
        </div>

        {stage === "email" ? (
          <form onSubmit={handleSendOtp} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>

            <button type="submit" className="submit-button">
              Send OTP
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="otp">OTP</label>
              <input
                type="text"
                id="otp"
                className="input-field"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                placeholder="Enter OTP"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                className="input-field"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <button type="submit" className="submit-button">
              Verify OTP
            </button>

            <div className="resend-otp">
              <button
                type="button"
                className="resend-button"
                onClick={handleResendOtp}
                disabled={resendTimer > 0 || resendAttempts >= 2}
              >
                {resendTimer > 0
                  ? `Resend OTP in ${resendTimer}s`
                  : "Resend OTP"}
              </button>
            </div>
          </form>
        )}

        <p className="login-link">
          Back to{" "}
          <a href="#" onClick={() => navigate("/login")}>
            Login
          </a>
        </p>
      </div>

      {showPopup && (
        <div className="forgot-password-popup">
          <div className="forgot-password-popup-content">
            {popupType === "loading" && animationStage === 1 && (
              <div className="loading-animation">
                <div className="spinner"></div>
                <div className="clothing-items">
                  <div className="clothing-item item1"></div>
                  <div className="clothing-item item2"></div>
                  <div className="clothing-item item3"></div>
                </div>
                <h2>{popupMessage}</h2>
                <p>Preparing your fashion journey</p>
              </div>
            )}

            {popupType === "success" && animationStage === 2 && (
              <div className="success-animation-container">
                <div className="success-checkmark">
                  <div className="check-icon">
                    <span className="icon-line line-tip"></span>
                    <span className="icon-line line-long"></span>
                    <div className="icon-circle"></div>
                    <div className="icon-fix"></div>
                  </div>
                </div>
                <div className="fashion-runway">
                  <div className="runway-model"></div>
                  <div className="runway-lights"></div>
                </div>
                <h2 className="success-title">{popupMessage}</h2>
                <p className="success-message">
                  Your style journey continues...
                </p>
              </div>
            )}

            {popupType === "error" && (
              <div className="error-animation-container">
                <div className="error-icon">
                  <span className="error-x-mark">
                    <span className="error-line left"></span>
                    <span className="error-line right"></span>
                  </span>
                </div>
                <h2>Error</h2>
                <p>{popupMessage}</p>
                <button
                  className="error-close-button"
                  onClick={() => setShowPopup(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="decorative-circle decorative-circle-1"></div>
      <div className="decorative-circle decorative-circle-2"></div>
      <div className="decorative-circle decorative-circle-3"></div>
    </div>
  );
}

export default ForgotPasswordPage;
